import { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Flex, Button } from "antd"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import CustomDrawer from "ui/Drawer"
import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./index.module.css"

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const TITLE = { day: "Select Day", week: "Select Week", month: "Select Month" }

// Build the 42-cell (6-week) grid for the month `viewDate` is in.
const buildMonthGrid = (viewDate) => {
  const start = viewDate.startOf("month").startOf("week")
  return Array.from({ length: 42 }, (_, i) => start.add(i, "day"))
}

// Self-contained Day / Week / Month picker bottom sheet for the mobile Bookings header.
// Emits the chosen date the same way the header's other controls do:
// onChange({ target: { name, value } }) with value = YYYY-MM-DD (start of the period).
const MobileDatePicker = ({ open, onClose, range = "month", value, name = "date", minDate = null, onChange }) => {
  const [viewDate, setViewDate] = useState(value || dayjs())
  const [selected, setSelected] = useState(value || dayjs())

  // Re-seed from the current value whenever the sheet reopens.
  useEffect(() => {
    if (!open) return
    const base = value || dayjs()
    setViewDate(base)
    setSelected(base)
  }, [open, value])

  const isMonthMode = range === "month"
  const grid = useMemo(() => (isMonthMode ? [] : buildMonthGrid(viewDate)), [isMonthMode, viewDate])

  const shiftView = (direction) => setViewDate((prev) => prev.add(direction, isMonthMode ? "year" : "month"))

  const isCellSelected = (cell) => {
    if (range === "week") return cell.startOf("week").isSame(selected.startOf("week"), "day")
    return cell.isSame(selected, "day")
  }

  // A cell is disabled when its whole period (day / week / month) falls before minDate.
  const isCellDisabled = (cell) => {
    if (!minDate) return false
    if (range === "week") return cell.endOf("week").isBefore(minDate, "day")
    if (range === "month") return cell.endOf("month").isBefore(minDate, "day")
    return cell.isBefore(minDate, "day")
  }

  const handleApply = () => {
    const unit = range === "week" ? "week" : range === "month" ? "month" : "day"
    const value_ = (unit === "day" ? selected : selected.startOf(unit)).format(DATE_FORMAT)
    onChange?.({ target: { name, value: value_ } })
    onClose?.()
  }

  return (
    <CustomDrawer open={open} setOpen={(next) => !next && onClose?.()} title={TITLE[range] || TITLE.month}>
      <div className={styles.sheet}>
        <Flex align="center" justify="space-between" className={styles.navRow}>
          <Button type="text" icon={<LeftOutlined />} onClick={() => shiftView(-1)} aria-label="Previous" />
          <span className={styles.navLabel}>{isMonthMode ? viewDate.format("YYYY") : viewDate.format("MMMM YYYY")}</span>
          <Button type="text" icon={<RightOutlined />} onClick={() => shiftView(1)} aria-label="Next" />
        </Flex>

        {isMonthMode ? (
          <div className={styles.monthGrid}>
            {MONTHS.map((label, index) => {
              const cell = viewDate.month(index)
              const active = cell.isSame(selected, "month")
              const disabled = isCellDisabled(cell)
              return (
                <button
                  key={label}
                  type="button"
                  disabled={disabled}
                  className={`${styles.monthCell} ${active ? styles.cellActive : ""} ${disabled ? styles.cellDisabled : ""}`}
                  onClick={() => setSelected(cell)}
                >
                  {label}
                </button>
              )
            })}
          </div>
        ) : (
          <div className={styles.calendar}>
            <div className={styles.weekHeader}>
              {WEEKDAYS.map((day, i) => (
                <span key={`${day}-${i}`} className={styles.weekday}>{day}</span>
              ))}
            </div>
            <div className={styles.dayGrid}>
              {grid.map((cell) => {
                const inMonth = cell.month() === viewDate.month()
                const active = isCellSelected(cell)
                const disabled = isCellDisabled(cell)
                return (
                  <button
                    key={cell.format("YYYY-MM-DD")}
                    type="button"
                    disabled={disabled}
                    className={`${styles.dayCell} ${inMonth ? "" : styles.dayMuted} ${active ? styles.cellActive : ""} ${disabled ? styles.cellDisabled : ""}`}
                    onClick={() => setSelected(cell)}
                  >
                    {cell.date()}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <Flex gap={12} className={styles.actions}>
          <Button block shape="round" onClick={onClose}>Cancel</Button>
          <Button block shape="round" type="primary" onClick={handleApply}>Apply</Button>
        </Flex>
      </div>
    </CustomDrawer>
  )
}

MobileDatePicker.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  range: PropTypes.oneOf(["day", "week", "month"]),
  value: PropTypes.object,
  name: PropTypes.string,
  minDate: PropTypes.object,
  onChange: PropTypes.func
}

export default MobileDatePicker
